import { Modal, message } from 'antd';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import receivableApi from '../../../apis/ReceivableApi';
import receivableNotificationBuildApi from '../../../apis/ReceivableNotificationBuildApi';
import receivableNotificationSignApi from '../../../apis/ReceivableNotificationSignApi';
import { ReceivableNotificationSign } from '../../../models/Entities';
import alertService from '../../../services/AlertService';
import receivableService from '../../../services/ReceivableService';
import styles from './ReceivableNotificationSignsModal.module.scss';

const ReceivableNotificationSignsModal = (props: Props) => {
    const { receivableIds, onCancel } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [loading, setLoading] = useState<'loading' | 'saving'>();

    /*** METHODS ***/

    const saveAll = async () => {
        const AutoScript = window.AutoScript as any;
        try {
            setLoading('saving');

            AutoScript.setStickySignatory(true);
            sign(receivableIds, 0);
        } catch (error: any) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const sign = async (receivableIds: number[], index: number) => {
        try {
            const receivableId: number = receivableIds[index];
            const receivableFull = await receivableApi.getFull(receivableId);
            const receivableNotificationBuild = receivableFull.events.at(-1);

            if (receivableNotificationBuild && receivableNotificationBuild.id && receivableNotificationBuild.state === 'NOTIFICATION_BUILD') {
                const document: Blob = await receivableNotificationBuildApi.download(receivableNotificationBuild.id); // to be replace by receivableNotificationId
                const notificationSign: ReceivableNotificationSign = {
                    receivableId: receivableId
                };

                const AutoScript = window.AutoScript as any;
                const documentBase64 = await receivableService.blobToBase64(document);
                const formattedDocumentBase64: string = (documentBase64 as string).replace('data:text/xml;base64,', '');
                const parameters: string[] = [
                    'signaturePositionOnPageLowerLeftX = 25',
                    'signaturePositionOnPageLowerLeftY = 40',
                    'signaturePositionOnPageUpperRightX = 330',
                    'signaturePositionOnPageUpperRightY = 65',
                    'signaturePages = 1',
                    'layer2FontSize = 8'
                ];
                const formattedParameters: string = parameters.join('\n');

                AutoScript.sign(
                    formattedDocumentBase64,
                    'SHA512withRSA',
                    'PAdES',
                    formattedParameters,
                    (signatureB64: string, certificateB64: string, extraData: any) => {
                        const signedDocument = receivableService.b64toBlob(signatureB64, 'application/pdf');
                        createNotificationSign(notificationSign, signedDocument).then(() => {
                            if (index < receivableIds.length - 1) {
                                sign(receivableIds, ++index);
                            } else {
                                AutoScript.setStickySignatory(false);
                                message.success(intl.formatMessage({ id: 'status.signed' }));
                                props.onSave();
                            }
                        });
                    },
                    (type: string, error: string) => {
                        AutoScript.setStickySignatory(false);
                        message.error(error);
                    }
                );
            }
        } catch (error: any) {
            message.error(error);
        }
    };

    const createNotificationSign = async (notificationSign: ReceivableNotificationSign, signedDocument: Blob) => {
        try {
            await receivableNotificationSignApi.create(notificationSign, signedDocument);
        } catch (error: any) {
            console.log(error);
        }
    };

    const cancel = async () => {
        onCancel();
    };

    /*** VISUAL ***/

    return (
        <Modal
            title={<FormattedMessage id="receivable.notificationSigns.modal.title" />}
            open={true}
            onCancel={cancel}
            onOk={saveAll}
            okText={<FormattedMessage id="button.sign" tagName="span" />}
            okButtonProps={{ loading: loading === 'saving', size: 'large' }}
            cancelButtonProps={{ disabled: loading === 'saving', size: 'large' }}
            width={600}
        >
            <div className={styles.container}>
                <FormattedMessage id="receivable.notificationSigns.modal.desc" />
            </div>
        </Modal>
    );
};
export default ReceivableNotificationSignsModal;

interface Props {
    receivableIds: number[];
    onSave: () => void;
    onCancel: () => void;
}
